import HeaderWordmark from '../shared/HeaderWordmark';
import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import AppNav from '../shared/AppNav';
import { useData } from '../../contexts/DataContext';
import useAppChrome from '../../hooks/useAppChrome';
import useDismiss from '../../hooks/useDismiss';
import { useUiV2 } from '../../config/uiVersion';
import useInventoryPage from '../../hooks/useInventoryPage';
import SummaryStrip from './SummaryStrip';
import AnomalyStrip from './AnomalyStrip';
import FilterControls from './FilterControls';
import InventoryTable from './InventoryTable';
import UnitDetailPane from './UnitDetailPane';
import InventoryPageV2 from '../InventoryV2/InventoryPageV2';
import './InventoryDashboard.css';

// The current (v1) inventory page. All state and behaviour live in
// hooks/useInventoryPage, shared with the redesigned page (InventoryPageV2).
function InventoryDashboardV1({ onLogout, user, isAuthenticated, orgName, isAdmin }) {
    const dataContext = useData();
    // Header state shared with the v2 top bar (hooks/useAppChrome): whose data
    // is on screen (for SAG staff a customer's dealership, which wins over the
    // org name from /me), read-only, the dealership switch and data freshness.
    const chrome = useAppChrome({ orgName, data: dataContext });
    const { dealership, readOnly, canSwitch, cacheStatus } = chrome;
    const {
        allData, loading, error, showLoadingGate, refresh: handleRefresh,
        filters, filteredData, displayedData, machineFacetData, machineCatalog, stats,
        changeFilters: handleFiltersChange, handleAgingClick, handleRiskTierClick,
        toggleQualifyingQuote, toggleRetargeting,
        anomalyFilter, handleAnomalyVerdictToggle, handleAnomalyGroupToggle, handleAnomalyClearSignals,
        openTaskCount, taskFilterActive, toggleTaskFilter,
        hasActiveFilters, handleClearAllFilters, resultCountRef,
        selectedUnit, initialTab, openUnit, closeUnit,
    } = useInventoryPage({ isAuthenticated });
    const [showUserMenu, setShowUserMenu] = useState(false);
    const userMenuRef = useRef(null);

    // Close user menu on outside click
    const closeUserMenu = useCallback(() => setShowUserMenu(false), []);
    useDismiss(userMenuRef, closeUserMenu);

    if (showLoadingGate) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading inventory data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <div className="error-message">
                    <h3>Something went wrong</h3>
                    <p>{error}</p>
                    <button onClick={handleRefresh} className="btn-primary">Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div className="inventory-dashboard">
            <header className="dashboard-header">
                <div className="header-left">
                    <HeaderWordmark />
                    <div>
                        <p className="subtitle">Predictive Pricing for Equipment Dealers</p>
                    </div>
                    {/* Whose inventory this is. Anyone with access to more than one
                        dealership otherwise has nothing on screen to notice by. */}
                    {dealership && <span className="header-org" title="Dealership currently in view">{dealership}</span>}
                    {/* The backend refuses writes here, so the write actions are gone
                        from the body. This says why rather than leaving it a mystery. */}
                    {readOnly && (
                        <span className="header-readonly" title="You can look at this dealership's data but not change it">
                            <span className="header-readonly-dot" aria-hidden="true" />
                            Read-only
                        </span>
                    )}
                    {/* Only for callers who have somewhere else to go. */}
                    {canSwitch && (
                        <button
                            type="button"
                            className="header-switch"
                            onClick={chrome.switchDealership}
                            title="Look at a different dealership"
                        >
                            Switch dealership
                        </button>
                    )}
                </div>
                <div className="header-right">
                    {cacheStatus && (
                        <span className={`cache-badge ${cacheStatus.stale ? 'cache-badge-stale' : ''}`} title={cacheStatus.title}>
                            Updated {cacheStatus.label}
                        </span>
                    )}
                    {isAdmin && (
                        <Link to="/admin" className="btn-header" title="Switch to the admin console">
                            <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15">
                                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.53 1.53 0 01-2.29.95c-1.37-.84-2.94.73-2.1 2.1.54.88.07 2.02-.95 2.29-1.56.38-1.56 2.6 0 2.98a1.53 1.53 0 01.95 2.29c-.84 1.37.73 2.94 2.1 2.1a1.53 1.53 0 012.29.95c.38 1.56 2.6 1.56 2.98 0a1.53 1.53 0 012.29-.95c1.37.84 2.94-.73 2.1-2.1a1.53 1.53 0 01.95-2.29c1.56-.38 1.56-2.6 0-2.98a1.53 1.53 0 01-.95-2.29c.84-1.37-.73-2.94-2.1-2.1a1.53 1.53 0 01-2.29-.95zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                            </svg>
                            Admin
                        </Link>
                    )}
                    <AppNav current="inventory" />
                    <button onClick={chrome.refresh} className="btn-header btn-refresh" title="Refresh Data">
                        <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15">
                            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                        </svg>
                        Refresh
                    </button>
                    <div className="user-menu-wrapper" ref={userMenuRef}>
                        <button
                            className="btn-header btn-user"
                            onClick={() => setShowUserMenu(!showUserMenu)}
                            aria-expanded={showUserMenu}
                        >
                            <span className="user-avatar">
                                {(user?.name || 'U')[0].toUpperCase()}
                            </span>
                            <span className="user-name">{user?.name || 'User'}</span>
                            <svg className={`chevron ${showUserMenu ? 'open' : ''}`} viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>

                        {showUserMenu && (
                            <div className="user-dropdown">
                                {cacheStatus && (
                                    <div className="dropdown-item dropdown-info">
                                        <span className="dropdown-label">Last updated</span>
                                        <span className="dropdown-value">{cacheStatus.time}</span>
                                    </div>
                                )}
                                <div className="dropdown-divider"></div>
                                <button className="dropdown-item dropdown-logout" onClick={onLogout}>
                                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                                        <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                                    </svg>
                                    Sign Out
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <main className="dashboard-content">
                <SummaryStrip
                    stats={stats}
                    data={filteredData}
                    onAgingClick={handleAgingClick}
                    activeAgingBuckets={filters.agingBucket || []}
                    onRiskTierClick={handleRiskTierClick}
                    activeRiskTier={filters.riskTier !== 'All' ? filters.riskTier : null}
                />

                <AnomalyStrip
                    data={allData}
                    activeVerdicts={anomalyFilter}
                    onVerdictToggle={handleAnomalyVerdictToggle}
                    onGroupToggle={handleAnomalyGroupToggle}
                    onClearSignals={handleAnomalyClearSignals}
                />

                <FilterControls
                    filters={filters}
                    onChange={handleFiltersChange}
                    allData={allData}
                    filteredCount={displayedData.length}
                    onQualifyingQuoteToggle={toggleQualifyingQuote}
                    onRetargetingToggle={toggleRetargeting}
                    hasActiveFilters={hasActiveFilters}
                    onClearAllFilters={handleClearAllFilters}
                    resultCountRef={resultCountRef}
                    taskFilterActive={taskFilterActive}
                    openTaskCount={openTaskCount}
                    onTaskFilterToggle={toggleTaskFilter}
                    machineFacetData={machineFacetData}
                    machineCatalog={machineCatalog}
                />

                <InventoryTable
                    data={displayedData}
                    onUnitClick={(unit, tab) => openUnit(unit, tab || 'ai')}
                    loading={loading}
                    onClearFilters={handleClearAllFilters}
                    retargetActive={filters.retargeting}
                />
            </main>

            {selectedUnit && (
                <UnitDetailPane
                    unit={selectedUnit}
                    onClose={closeUnit}
                    initialTab={initialTab}
                />
            )}

            <footer className="dashboard-footer">
                <span>&copy; 2026 Fusable. All rights reserved.</span>
            </footer>
        </div>
    );
}

const propTypes = {
    onLogout: PropTypes.func.isRequired,
    user: PropTypes.shape({
        name: PropTypes.string,
    }),
    isAuthenticated: PropTypes.bool,
    isAdmin: PropTypes.bool,
    orgName: PropTypes.string,
};

InventoryDashboardV1.propTypes = propTypes;
InventoryDashboardV1.defaultProps = {
    user: null,
    isAuthenticated: false,
};

/**
 * The inventory page. With the "new design" flag on (config/uiVersion.js)
 * it is the redesigned page; otherwise exactly today's page.
 */
function InventoryDashboard(props) {
    const uiV2 = useUiV2();
    return uiV2 ? <InventoryPageV2 {...props} /> : <InventoryDashboardV1 {...props} />;
}

InventoryDashboard.propTypes = propTypes;
InventoryDashboard.defaultProps = {
    user: null,
    isAuthenticated: false,
};

export default InventoryDashboard;
